import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Star, MessageCircle, Users, Heart } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALUMNI_WHATSAPP = "https://chat.whatsapp.com/ImnckAY8hHcCIzxPZQE33D";

const ratingOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const DeparturePage = () => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    room: "",
    stay_duration: "",
    // Ratings
    overall: null,
    space: null,
    coworking: null,
    community: null,
    value: null,
    // Open questions
    came_for: "",
    got_instead: "",
    best_moment: "",
    would_change: "",
    would_recommend: "",
    testimonial: "",
    testimonial_permission: false,
    // Alumni
    join_alumni: false,
    current_project: "",
    location_next: ""
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/departure/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      setDone(true);
    } catch {
      setDone(true);
    }
    setSubmitting(false);
  };

  const RatingRow = ({ label, field }) => (
    <div className="mb-5">
      <p className="text-[#2E2A26] text-sm font-medium mb-2">{label}</p>
      <div className="flex gap-1.5">
        {ratingOptions.map(n => (
          <button
            key={n}
            onClick={() => update(field, n)}
            className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
              form[field] === n
                ? 'bg-[#C85A38] text-white'
                : form[field] && form[field] >= n
                  ? 'bg-[#C85A38]/20 text-[#C85A38]'
                  : 'bg-[#F4ECE2] text-[#2E2A26]/40 hover:bg-[#C85A38]/10'
            }`}
            data-testid={`rating-${field}-${n}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );

  if (done) {
    return (
      <div className="min-h-screen bg-[#FBF7F1] flex items-center justify-center px-4">
        <Helmet>
          <title>Thank You | Savi Coliving</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="max-w-md text-center">
          <Heart className="w-10 h-10 text-[#C85A38] mx-auto mb-4" />
          <h1 className="font-serif text-3xl text-[#2E2A26] mb-3">Thank you, {form.name.split(' ')[0] || 'friend'}.</h1>
          <p className="text-[#2E2A26]/60 text-sm mb-8">
            Your feedback means a lot. Seriously. It's how we keep making this place better.
          </p>
          
          {form.join_alumni && (
            <div className="bg-[#F4ECE2] rounded-2xl p-6 mb-4">
              <Users className="w-6 h-6 text-[#C85A38] mx-auto mb-3" />
              <p className="text-[#2E2A26] font-medium text-sm mb-2">Join the Savi Alumni group</p>
              <p className="text-[#2E2A26]/50 text-xs mb-4">Monthly updates, alumni meetups, and first dibs on returning.</p>
              <a
                href={ALUMNI_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white text-sm font-medium rounded-full px-6 py-3 transition-colors"
                data-testid="alumni-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> Join WhatsApp Group
              </a>
            </div>
          )}

          <div className="bg-[#F4ECE2] rounded-2xl p-6 mb-6">
            <Star className="w-6 h-6 text-[#C85A38] mx-auto mb-3" />
            <p className="text-[#2E2A26] font-medium text-sm mb-2">Leave a Google Review</p>
            <p className="text-[#2E2A26]/50 text-xs mb-4">It takes 30 seconds and genuinely helps other people find us.</p>
            <a
              href="https://g.page/r/Cdp75wG6jBNyEBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white border border-stone-200 hover:border-[#C85A38] text-[#2E2A26] text-sm font-medium rounded-full px-6 py-3 transition-colors"
              data-testid="google-review-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Review on Google
            </a>
          </div>

          <Link to="/" className="text-[#C85A38] text-sm hover:underline">Back to savicoliving.com</Link>
        </div>
      </div>
    );
  }

  const steps = [
    // Step 0: Who are you
    () => (
      <div>
        <h2 className="font-serif text-2xl text-[#2E2A26] mb-2">Before you go.</h2>
        <p className="text-[#2E2A26]/50 text-sm mb-6">This takes about 3 minutes. Your honesty makes Savi better for the next person.</p>
        
        <div className="space-y-4">
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">Your name</label>
            <input value={form.name} onChange={e => update('name', e.target.value)} placeholder="Steve Haynes"
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38]" data-testid="input-name" />
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">Email</label>
            <input type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="you@email.com"
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38]" data-testid="input-email" />
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">Which room were you in?</label>
            <select value={form.room} onChange={e => update('room', e.target.value)}
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38]" data-testid="input-room">
              <option value="">Select room</option>
              <option>Garden Room</option>
              <option>Sky Room</option>
              <option>Panorama Double</option>
              <option>Tower Room</option>
              <option>Junior Suite (Baylight)</option>
              <option>Luxury Suite</option>
              <option>Apartment</option>
            </select>
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">How long did you stay?</label>
            <select value={form.stay_duration} onChange={e => update('stay_duration', e.target.value)}
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38]" data-testid="input-duration">
              <option value="">Select</option>
              <option>1 month</option>
              <option>2 months</option>
              <option>3+ months</option>
            </select>
          </div>
        </div>
      </div>
    ),

    // Step 1: Ratings
    () => (
      <div>
        <h2 className="font-serif text-2xl text-[#2E2A26] mb-2">Rate your experience.</h2>
        <p className="text-[#2E2A26]/50 text-sm mb-6">1 = terrible, 10 = life-changing. Be honest.</p>
        
        <RatingRow label="Overall experience at Savi" field="overall" />
        <RatingRow label="The space itself (room, villa, surroundings)" field="space" />
        <RatingRow label="Coworking setup (desk, WiFi, screens)" field="coworking" />
        <RatingRow label="The community & people" field="community" />
        <RatingRow label="Value for money" field="value" />
      </div>
    ),

    // Step 2: The real questions
    () => (
      <div>
        <h2 className="font-serif text-2xl text-[#2E2A26] mb-2">The real stuff.</h2>
        <p className="text-[#2E2A26]/50 text-sm mb-6">These are the answers that actually help us grow.</p>

        <div className="space-y-5">
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">What did you come to Savi hoping to get?</label>
            <textarea value={form.came_for} onChange={e => update('came_for', e.target.value)} rows={2} placeholder="Focus, clarity, community, a change of scenery..."
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38] resize-none" data-testid="input-came-for" />
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">What did you actually get? (Be honest — it might be different)</label>
            <textarea value={form.got_instead} onChange={e => update('got_instead', e.target.value)} rows={2} placeholder="What surprised you, what was different than expected..."
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38] resize-none" data-testid="input-got-instead" />
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">Best moment or memory from your stay?</label>
            <textarea value={form.best_moment} onChange={e => update('best_moment', e.target.value)} rows={2} placeholder="A conversation, a hike, a breakthrough on your project..."
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38] resize-none" data-testid="input-best-moment" />
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">If you could change one thing about Savi, what would it be?</label>
            <textarea value={form.would_change} onChange={e => update('would_change', e.target.value)} rows={2} placeholder="Anything. We can take it."
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38] resize-none" data-testid="input-would-change" />
          </div>
          <div>
            <label className="text-[#2E2A26]/60 text-xs mb-1 block">Would you recommend Savi? If yes — what would you tell them?</label>
            <textarea value={form.would_recommend} onChange={e => update('would_recommend', e.target.value)} rows={2} placeholder="What you'd say to a friend considering it..."
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38] resize-none" data-testid="input-recommend" />
          </div>
        </div>
      </div>
    ),

    // Step 3: Testimonial + Alumni
    () => (
      <div>
        <h2 className="font-serif text-2xl text-[#2E2A26] mb-2">One last thing.</h2>
        <p className="text-[#2E2A26]/50 text-sm mb-6">This part is optional but means a lot.</p>

        <div className="space-y-6">
          {/* Testimonial */}
          <div className="bg-[#F4ECE2] rounded-xl p-5">
            <Star className="w-5 h-5 text-[#C85A38] mb-2" />
            <p className="text-[#2E2A26] text-sm font-medium mb-1">Leave a testimonial</p>
            <p className="text-[#2E2A26]/40 text-xs mb-3">In your own words — what was Savi like? We might use this on the website.</p>
            <textarea value={form.testimonial} onChange={e => update('testimonial', e.target.value)} rows={3} placeholder="Write whatever feels true..."
              className="w-full bg-white border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#C85A38] resize-none" data-testid="input-testimonial" />
            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input type="checkbox" checked={form.testimonial_permission} onChange={e => update('testimonial_permission', e.target.checked)}
                className="w-4 h-4 rounded border-stone-300 text-[#C85A38] focus:ring-[#C85A38]" data-testid="checkbox-permission" />
              <span className="text-[#2E2A26]/50 text-xs">You can use my first name and words on the Savi website</span>
            </label>
          </div>

          {/* Alumni */}
          <div className="bg-[#F4ECE2] rounded-xl p-5">
            <Users className="w-5 h-5 text-[#C85A38] mb-2" />
            <p className="text-[#2E2A26] text-sm font-medium mb-1">Join the Savi Alumni</p>
            <p className="text-[#2E2A26]/40 text-xs mb-3">A WhatsApp group for past guests. Monthly updates from Steve, alumni meetups, and first dibs when you want to come back.</p>
            <label className="flex items-center gap-2 cursor-pointer mb-3">
              <input type="checkbox" checked={form.join_alumni} onChange={e => update('join_alumni', e.target.checked)}
                className="w-4 h-4 rounded border-stone-300 text-[#C85A38] focus:ring-[#C85A38]" data-testid="checkbox-alumni" />
              <span className="text-[#2E2A26]/70 text-sm">Yes, I'm in</span>
            </label>
            
            {form.join_alumni && (
              <div className="space-y-3 mt-3 pt-3 border-t border-stone-200">
                <div>
                  <label className="text-[#2E2A26]/60 text-xs mb-1 block">What are you working on next?</label>
                  <input value={form.current_project} onChange={e => update('current_project', e.target.value)} placeholder="Project, company, or just 'figuring it out'"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C85A38]" data-testid="input-project" />
                </div>
                <div>
                  <label className="text-[#2E2A26]/60 text-xs mb-1 block">Where are you headed?</label>
                  <input value={form.location_next} onChange={e => update('location_next', e.target.value)} placeholder="City, country, or 'no idea yet'"
                    className="w-full bg-white border border-stone-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#C85A38]" data-testid="input-location" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  ];

  const canProceed = () => {
    if (step === 0) return form.name && form.email;
    if (step === 1) return form.overall;
    return true;
  };

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Departure Feedback | Savi Coliving</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Header */}
      <header className="bg-[#FBF7F1] border-b border-stone-100">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png" alt="Savi" className="h-7 w-auto" />
            <span className="text-base tracking-wider text-[#C89B7B]"><span style={{fontFamily: 'Cinzel, serif'}}>SAVI</span> <span style={{fontFamily: "'Cormorant Garamond', serif"}}>Coliving</span></span>
          </Link>
          <span className="text-[#2E2A26]/30 text-xs">{step + 1} of {steps.length}</span>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 pt-2">
        <div className="flex gap-1.5">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-[#C85A38]' : 'bg-[#2E2A26]/10'}`} />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        {steps[step]()}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="text-[#2E2A26]/40 text-sm hover:text-[#2E2A26]">
              ← Back
            </button>
          ) : <div />}
          
          {step < steps.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-[#C85A38] hover:bg-[#A84830] disabled:opacity-30 text-white text-sm font-medium rounded-full px-8 py-3 transition-all flex items-center gap-2"
              data-testid="next-step-btn"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#C85A38] hover:bg-[#A84830] disabled:opacity-50 text-white text-sm font-medium rounded-full px-8 py-3 transition-all"
              data-testid="submit-feedback-btn"
            >
              {submitting ? "Sending..." : "Submit feedback"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeparturePage;
